import { useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/i18n";
import { Button } from "@/components/ui/button";
import { Image, Video } from "lucide-react";

// Import refactored page components
import { 
  HomePage, 
  CommunityPage, 
  PostDetailPage,
  RecipeDetailPage,
  ProductDetailPage,
  OrdersPage,
  MessagesPage,
  UserProfilePage,
  AdminPage,
  LoginPage,
  CheckoutPage,
  OrderSuccessPage,
  SearchPage,
  CreatorPage,
  CreatorCenterPage,
  ArtCanvasPage
} from '@/pages';
import ArtworkDetailPage from '@/pages/ArtworkDetailPage';
import ArtworksPage from '@/pages/ArtworksPage';
import CategoryProductsPage from '@/pages/CategoryProductsPage';
import FarmDetailPage from '@/pages/FarmDetailPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ExhibitionsPage from '@/pages/ExhibitionsPage';
import ExhibitionDetailPage from '@/pages/ExhibitionDetailPage';
import CreateExhibitionPage from '@/pages/CreateExhibitionPage';
import ArtCommunityListPage from '@/pages/ArtCommunityListPage';
import UserTypeListPage from '@/pages/UserTypeListPage';
import NotificationsPage from '@/pages/NotificationsPage';
import CustomizePage from '@/pages/CustomizePage';
import CustomizeSuccessPage from '@/pages/CustomizeSuccessPage';
import ArtworkPurchaseSuccessPage from '@/pages/ArtworkPurchaseSuccessPage';
import CustomServicePage from '@/pages/CustomServicePage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import WhyKwhPage from '@/pages/WhyKwhPage';
import RegionExplorePage from '@/pages/RegionExplorePage';
import SilkRoadPage from '@/pages/SilkRoadPage';
import { Header } from '@/components/layout';
import Footer from '@/components/home/Footer';
import { AuthProvider, CartProvider, TranslationProvider } from '@/contexts';
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt';
import LanguageConfirmDialog from '@/components/common/LanguageConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// File Upload Component
const FileUploader = ({ onUpload, accept, multiple = false, type = "image", label }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      if (multiple) {
        const formData = new FormData();
        Array.from(files).forEach(file => formData.append('files', file));
        
        const res = await axios.post(`${API}/upload/multiple`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        
        const urls = res.data.files.map(f => `${BACKEND_URL}${f.url}`);
        onUpload(urls);
        toast.success(`成功上传 ${urls.length} 个文件`);
      } else {
        const formData = new FormData();
        formData.append('file', files[0]);
        
        const endpoint = type === 'video' ? '/upload/video' : '/upload/image';
        const res = await axios.post(`${API}${endpoint}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        
        const url = `${BACKEND_URL}${res.data.url}`;
        onUpload(url);
        toast.success('上传成功');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || '上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full"
      >
        {uploading ? (
          <>
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
            上传中...
          </>
        ) : (
          <>
            {type === 'video' ? <Video className="w-4 h-4 mr-2" /> : <Image className="w-4 h-4 mr-2" />}
            {label || (type === 'video' ? '上传视频' : '上传图片')}
          </>
        )}
      </Button>
    </div>
  );
};
// Main App
function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <TranslationProvider>
              <div className="App">
                <Toaster position="top-center" richColors />
                <PWAInstallPrompt />
                <LanguageConfirmDialog />
                <Routes>
                <Route path="/" element={<><Header /><HomePage /></>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/checkout" element={<><Header /><CheckoutPage /></>} />
                <Route path="/order-success/:orderId" element={<><Header /><OrderSuccessPage /></>} />
                <Route path="/orders" element={<><Header /><OrdersPage /></>} />
                <Route path="/recipe/:recipeId" element={<><Header /><RecipeDetailPage /></>} />
                <Route path="/product/:productId" element={<><Header /><ProductDetailPage /></>} />
                <Route path="/artworks" element={<><Header /><ArtworksPage /></>} />
                <Route path="/artcanvas" element={<><Header /><ArtCanvasPage /></>} />
                <Route path="/artcanvas/:type" element={<><Header /><ArtCommunityListPage /></>} />
                <Route path="/artwork/:artworkId" element={<><Header /><ArtworkDetailPage /></>} />
                <Route path="/products" element={<CategoryProductsPage />} />
                <Route path="/community" element={<><Header /><CommunityPage /></>} />
                <Route path="/community/post/:postId" element={<><Header /><PostDetailPage /></>} />
                <Route path="/exhibitions" element={<><Header /><ExhibitionsPage /></>} />
                <Route path="/exhibitions/create" element={<><Header /><CreateExhibitionPage /></>} />
                <Route path="/exhibitions/:exhibitionId" element={<><Header /><ExhibitionDetailPage /></>} />
                <Route path="/farm/:farmId" element={<><Header /><FarmDetailPage /></>} />
                <Route path="/profile/:userId" element={<><Header /><UserProfilePage /></>} />
                <Route path="/messages" element={<><Header /><MessagesPage /></>} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/search" element={<><Header /><SearchPage /></>} />
                <Route path="/creator/:creatorId" element={<><Header /><CreatorPage /></>} />
                <Route path="/creator-center" element={<><Header /><CreatorCenterPage /></>} />
                <Route path="/customize/:artworkId" element={<><Header /><CustomizePage /></>} />
                <Route path="/customize/success" element={<><Header /><CustomizeSuccessPage /></>} />
                <Route path="/artwork-purchase/success" element={<><Header /><ArtworkPurchaseSuccessPage /></>} />
                <Route path="/custom-service" element={<><Header /><CustomServicePage /></>} />
                <Route path="/why-kwh" element={<><Header /><WhyKwhPage /></>} />
                <Route path="/explore" element={<><Header /><SilkRoadPage /></>} />
                <Route path="/explore/:regionSlug" element={<><Header /><RegionExplorePage /></>} />
                <Route path="/users/:userType" element={<UserTypeListPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
              </Routes>
              <Footer />
            </div>
          </TranslationProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
